<template>
  <v-card elevation="0" class="filter-banner">
    <v-card-text class="px-4 py-2">
      <v-row>
        <v-col sm="2" md="2" lg="2" xl="1">
          <h3 class="py-1">Filters: </h3>
        </v-col>

        <v-col sm="5" md="2" lg="2" xl="3">
          <v-skeleton-loader
            v-if="coachingDefinitionsDataLoadingStatus !== 'loaded' || userDataLoadingStatus !== 'loaded' || !filtersReady || !controlsAreReady"
            type="date-picker"
            height="56px"
          />
          <v-select
            v-else
            v-model="selectedCoachingDefinitionIds"
            :items="sortedCoachingDefinitions"
            :label="getCoachingDefControlLabel"
            multiple
            item-text="title"
            item-value="id"
            dense
            small-chips
            persistent-hint
            :hint="coachingDefinitionsHint"
            :menu-props="{ openOnHover: true, closeDelay: 250 }"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip small v-if="index < 1">
                <span>{{ getCoachingDefinitionTitle(item.id) }}</span>
              </v-chip>
              <span v-if="index === 1"><strong> &hellip;</strong></span>
            </template>
          </v-select>
        </v-col>

        <v-col sm="5" md="2" lg="2" xl="3">
          <v-skeleton-loader
            v-if="taskTitlesDataLoadingStatus!='loaded' || userDataLoadingStatus!='loaded' || !filtersReady || !controlsAreReady"
            type="date-picker"
            height="56px"
          />
          <v-select
            v-else
            v-model="selectedTaskTitleIds"
            :items="sortedTaskTitles"
            :loading="(taskTitlesDataLoadingStatus!='loaded' && userDataLoadingStatus!='loaded')"
            :label="getTaskTitleControlLabel"
            multiple
            item-text="title"
            item-value="title_id"
            dense
            small-chips
            persistent-hint
            :hint="taskTitlesHint"
            :menu-props="{ openOnHover: true, closeDelay: 250 }"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip small v-if="index < 1">
                <span>{{ getTaskTitle(item.title_id) }}</span>
              </v-chip>
              <span v-if="index === 1"><strong> &hellip;</strong></span>
            </template>
          </v-select>
        </v-col>

        <v-col>
          <v-skeleton-loader
            v-if="taskTitlesDataLoadingStatus!='loaded' || userDataLoadingStatus!='loaded' || !filtersReady || !controlsAreReady"
            type="date-picker"
            height="56px"
          />
          <v-menu v-else
            :close-on-content-click="false"
            small
            v-model="createDateStartMenu"
            transition="scale-transition"
            offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field :value="formattedCreateDateStart" label="Start Date Created"
                            readonly
                            v-bind="attrs" v-on="on"
                            class="small-text-field pt-0"></v-text-field>
            </template>
            <v-date-picker
              small
              dense
              show-adjacent-months
              v-model="createDateStartDate"
              :allowed-dates="allowedStartDates"
              locale="en"
              class="small-date-picker"
              @change="createDateStartMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <v-col>
          <v-skeleton-loader
            v-if="taskTitlesDataLoadingStatus!='loaded' || userDataLoadingStatus!='loaded' || !filtersReady || !controlsAreReady"
            type="date-picker"
            height="56px"
          />
          <v-menu v-else
            :close-on-content-click="false"
            small
            v-model="createDateEndMenu"
            transition="scale-transition"
            offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field :value="formattedCreateDateEnd" label="End Date Created" readonly
                            v-bind="attrs" v-on="on"
                            class="small-text-field pt-0"></v-text-field>
            </template>
            <v-date-picker
              small
              dense
              show-adjacent-months
              v-model="createDateEndDate"
              :allowed-dates="allowedEndDates"
              locale="en"
              class="small-date-picker"
              @change="createDateEndMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <v-col class="d-flex justify-center align-center">
          <v-btn
            class="mr-2"
            color="primary"
            x-small
            :loading="loading"
            @click="clearSelectors"
          >
            Clear Filters
          </v-btn>
          <v-btn
            class="mr-2"
            color="primary"
            x-small
            :loading="loading"
            @click="reloadData"
          >
            Apply & Reload
          </v-btn>
        </v-col>

      </v-row>

    </v-card-text>
  </v-card>
</template>
<script>

const COACHING_HORIZON = 18 // months
const DEFAULT_HORIZON = 30 // days

import moment from "moment";
import CacheHandler from "@/utils/CacheHandler";
import {mapActions, mapGetters} from "vuex";

export default {
  name: "Filters",
  components: {},
  data() {
    return {
      selectedCoachingDefinitionIds: [],
      selectedTaskTitleIds: [],
      createDateStartMenu: false,
      createDateEndMenu: false,
      createDateStartDate: null,
      createDateEndDate: null,
      loading: false,
      controlsAreReady: false,
    }
  },
  methods: {
    ...mapActions('taskSummaryFilters', [
      'setCoachingDefinitionFilters',
      'setTaskTitleFilters',
      'setStartDateFilter',
      'setEndDateFilter',
      'setFiltersReady',
    ]),
    reloadData() { // TODO: find out why filters are getting reset
      const tmpFilters = {
        coaching_definition_ids: [...this.selectedCoachingDefinitionIds],
        task_title_ids: [...this.selectedTaskTitleIds],
        start_date: this.createDateStartDate,
        end_date: this.createDateEndDate,
      }
      this.updateSelectedCoachingDefinitionIds(tmpFilters.coaching_definition_ids)
      this.updateSelectedTaskTitleIds(tmpFilters.task_title_ids)
      this.updateCreateDateStartDate(tmpFilters.start_date)
      this.updateCreateDateEndDate(tmpFilters.end_date)
      this.setLoading(true)
      this.$emit('reload-data');
    },
    setLoading(val) {
      this.loading = val
    },
    getCoachingDefinitionTitle(id) {
      const defn = this.coachingDefinitionsData.find(d => d.id === id)
      return defn ? defn.title : 'Unknown'
    },
    getTaskTitle(id) {
      const title = this.taskTitlesData.find(d => d.title_id === id)
      return title ? title.title : 'Unknown'
    },
    allowedStartDates(dateString) {
      const date = moment(dateString, 'YYYY-MM-DD')
      const earliest = moment().subtract(COACHING_HORIZON, 'months')
      const latest = moment()                        // latest = today
      // Must not be before earliest or after today
      if (date.isBefore(earliest, 'day') || date.isAfter(latest, 'day')) {
        return false
      }
      // If user has chosen a end date, ensure start <= end
      if (this.createDateEndDate) {
        const end = moment(this.createDateEndDate, 'YYYY-MM-DD')
        if (date.isAfter(end, 'day')) {
          return false
        }
        if (end.diff(date, 'months') > COACHING_HORIZON) {
          return false
        }
      }
      return true
    },
    allowedEndDates(dateString) {
      const date = moment(dateString, 'YYYY-MM-DD')
      const earliest = moment().subtract(COACHING_HORIZON, 'months')
      const latest = moment()
      if (date.isBefore(earliest, 'day') || date.isAfter(latest, 'day')) {
        return false
      }
      if (this.createDateStartDate) {
        const start = moment(this.createDateStartDate, 'YYYY-MM-DD')
        if (date.isBefore(start, 'day')) {
          return false
        }
        if (date.diff(start, 'months') > COACHING_HORIZON) {
          return false
        }
      }
      return true
    },
    clearSelectors() {
      this.selectedCoachingDefinitionIds = []
      this.selectedTaskTitleIds = []
      this.createDateStartDate = moment().subtract(DEFAULT_HORIZON, 'days').format("YYYY-MM-DD")
      this.createDateEndDate = moment().format("YYYY-MM-DD")
    },
    updateSelectedCoachingDefinitionIds(ids) {
      this.setCoachingDefinitionFilters(ids)
      CacheHandler.setItem("selectedCoachingDefinitionIds", ids.join(','))
    },
    updateSelectedTaskTitleIds(ids) {
      this.setTaskTitleFilters(ids)
      CacheHandler.setItem("selectedTaskTitleIds", ids.join(','))
    },
    updateCreateDateStartDate(date) {
      this.setStartDateFilter(date)
      CacheHandler.setItem("createDateStartDate", date, 'session')
    },
    updateCreateDateEndDate(date) {
      this.setEndDateFilter(date)
      CacheHandler.setItem("createDateEndDate", 'session')
    },
    formattedDate(date) {
      return (date) ? moment(date).format("ddd, MMM Do YYYY") : ""
    },
    loadControls() {
      if(this.filtersReady) {
        this.selectedCoachingDefinitionIds = this.coachingDefinitionFilters
        this.selectedTaskTitleIds = this.taskTitleFilters
        this.createDateStartDate = this.startDateFilter
        this.createDateEndDate = this.endDateFilter
        this.controlsAreReady = true
      }
    },
    updateStartDateControl() {
      this.createDateStartDate = this.startDateFilter
    },
    updateEndDateControl() {
      this.createDateEndDate = this.endDateFilter
    }
  },
  computed: {
    ...mapGetters({
      userData: 'users/userData',
      userDataLoadingStatus: 'users/userDataLoadingStatus',
      userDataLoadingError: 'users/userDataLoadingError',
      userDataLoadingErrorStatus: 'users/userDataLoadingErrorStatus',
    }),
    ...mapGetters({
      coachingDefinitionFilters: 'taskSummaryFilters/coachingDefinitionFilters',
      taskTitleFilters: 'taskSummaryFilters/taskTitleFilters',
      startDateFilter: 'taskSummaryFilters/startDateFilter',
      endDateFilter: 'taskSummaryFilters/endDateFilter',
      filtersReady: 'taskSummaryFilters/filtersReady',
      coachingDefinitionsData: 'taskSummaryData/coachingDefinitionsData',
      coachingDefinitionsDataLoadingStatus: 'taskSummaryData/coachingDefinitionsDataLoadingStatus',
      taskTitlesData: 'taskSummaryData/taskTitlesData',
      taskTitlesDataLoadingStatus: 'taskSummaryData/taskTitlesDataLoadingStatus',
    }),
    getCoachingDefControlLabel() {
      return (this.selectedCoachingDefinitionIds.length>0) ? 'Selected Coaching Definitions' : 'Any Coaching Definition'
    },
    getTaskTitleControlLabel() {
      return (this.selectedTaskTitleIds.length>0) ? 'Selected Task Types' : 'Any Task Type'
    },
    sortedCoachingDefinitions() {
      return this.coachingDefinitionsData.slice().sort((a, b) => {
        return a.title.localeCompare(b.title);
      })
    },
    sortedTaskTitles() {
      return this.taskTitlesData.slice().sort((a, b) => {
        return a.title.localeCompare(b.title);
      })
    },
    coachingDefinitionsHint() {
      const length = this.selectedCoachingDefinitionIds.length
      if (length === 0) return ''
      else if (length === 1) return '1 item selected'
      else return length + ' items selected'
    },
    taskTitlesHint() {
      const length = this.selectedTaskTitleIds.length
      if (length === 0) return ''
      else if (length === 1) return '1 item selected'
      else return length + ' items selected'
    },
    formattedCreateDateStart() {
      return this.formattedDate(this.createDateStartDate)
    },
    formattedCreateDateEnd() {
      return this.formattedDate(this.createDateEndDate)
    },

  },
  mounted() {
    this.loadControls()
  },
  watch: {
    filtersReady: 'loadControls',
    startDateFilter: 'updateStartDateControl',
    endDateFilter: 'updateEndDateControl',
  }
};

</script>

<style scoped>

.small-text-field ::v-deep .v-input__control {
  font-size: .9rem; /* Smaller font size for the text field */
  padding: 0 8px; /* Less padding for more compact look */
}

.small-text-field ::v-deep .v-label {
  font-size: .9rem; /* Smaller font for the label */
}

.small-text-field ::v-deep .v-input__control .v-input__prepend-outer {
  font-size: .9rem; /* Adjust icon size if necessary */
}

.filter-banner {
  border-radius: 8px !important;
  background-color: rgba(1, 106, 175, 0.05);
}

</style>
